import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Loader2, Search, Users, Crown, Star, User, Shield, Edit } from 'lucide-react';

interface UserProfile {
  id: string;
  display_name: string;
  email: string;
  subscription_tier: string;
  total_paid: number;
  is_premium: boolean;
  premium_until: string | null;
  stripe_customer_id: string | null;
  subscription_started_at: string | null;
  created_at: string;
  roles: string[];
}

const TIER_LABELS: Record<string, { label: string; price: string; icon: React.ReactNode }> = {
  free: { label: 'Gratis', price: '0 kr/mån', icon: <User className="h-4 w-4" /> },
  basic: { label: 'Bas', price: '29 kr/mån', icon: <Star className="h-4 w-4" /> },
  premium: { label: 'Premium', price: '149 kr/mån', icon: <Crown className="h-4 w-4" /> },
};

export function UserManagementAdmin() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTier, setFilterTier] = useState<string>('all');
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [newTier, setNewTier] = useState<string>('');
  const [newTotalPaid, setNewTotalPaid] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_all_profiles');
      
      if (error) throw error;
      
      setUsers((data as UserProfile[]) || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Kunde inte hämta användare');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEditUser = (user: UserProfile) => {
    setEditingUser(user);
    setNewTier(user.subscription_tier);
    setNewTotalPaid(user.total_paid.toString());
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_update_user_subscription', {
        target_user_id: editingUser.id,
        new_tier: newTier,
        new_total_paid: parseInt(newTotalPaid) || 0,
      });

      if (error) throw error;

      toast.success('Användare uppdaterad');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Kunde inte uppdatera användare');
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTier = filterTier === 'all' || user.subscription_tier === filterTier;
    return matchesSearch && matchesTier;
  });

  const stats = {
    total: users.length,
    free: users.filter(u => u.subscription_tier === 'free').length,
    basic: users.filter(u => u.subscription_tier === 'basic').length,
    premium: users.filter(u => u.subscription_tier === 'premium').length,
    totalRevenue: users.reduce((sum, u) => sum + u.total_paid, 0),
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString('sv-SE')} kr`;
  };

  const getRoleBadges = (roles: string[]) => {
    return roles.map(role => {
      if (role === 'admin') {
        return (
          <Badge key={role} variant="destructive" className="gap-1">
            <Shield className="h-3 w-3" />
            Admin
          </Badge>
        );
      }
      if (role === 'moderator') {
        return (
          <Badge key={role} variant="secondary" className="gap-1">
            Moderator
          </Badge>
        );
      }
      return null;
    });
  };

  const getTierBadge = (tier: string) => {
    const tierInfo = TIER_LABELS[tier] || TIER_LABELS.free;
    const variant = tier === 'premium' ? 'default' : tier === 'basic' ? 'secondary' : 'outline';
    
    return (
      <Badge variant={variant} className="gap-1">
        {tierInfo.icon}
        {tierInfo.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Totalt</span>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Gratis</span>
            </div>
            <p className="text-2xl font-bold">{stats.free}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Bas</span>
            </div>
            <p className="text-2xl font-bold">{stats.basic}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Premium</span>
            </div>
            <p className="text-2xl font-bold">{stats.premium}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Total intäkt</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
          </CardContent>
        </Card>
      </div>

      {/* User List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display">Användare</CardTitle>
          <CardDescription>
            Hantera användarnas prenumerationer och roller
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Sök namn eller e-post..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filtrera nivå" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla nivåer</SelectItem>
                <SelectItem value="free">Gratis</SelectItem>
                <SelectItem value="basic">Bas</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Användare</TableHead>
                  <TableHead>Nivå</TableHead>
                  <TableHead>Roller</TableHead>
                  <TableHead className="text-right">Betalt</TableHead>
                  <TableHead>Registrerad</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Inga användare hittades
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{user.display_name || 'Okänd'}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getTierBadge(user.subscription_tier)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.roles.length > 0 ? getRoleBadges(user.roles) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(user.total_paid)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditUser(user)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Redigera användare</DialogTitle>
                              <DialogDescription>
                                {editingUser?.display_name} ({editingUser?.email})
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label>Prenumerationsnivå</Label>
                                <Select value={newTier} onValueChange={setNewTier}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="free">
                                      <div className="flex items-center gap-2">
                                        <User className="h-4 w-4" />
                                        Gratis (0 kr/mån)
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="basic">
                                      <div className="flex items-center gap-2">
                                        <Star className="h-4 w-4" />
                                        Bas (29 kr/mån)
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="premium">
                                      <div className="flex items-center gap-2">
                                        <Crown className="h-4 w-4" />
                                        Premium (149 kr/mån)
                                      </div>
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Total betalning (kr)</Label>
                                <Input
                                  type="number"
                                  value={newTotalPaid}
                                  onChange={(e) => setNewTotalPaid(e.target.value)}
                                  min="0"
                                />
                                <p className="text-sm text-muted-foreground">
                                  Totalt belopp som användaren har betalat
                                </p>
                              </div>
                              {editingUser?.stripe_customer_id && (
                                <div className="space-y-2">
                                  <Label>Stripe Customer ID</Label>
                                  <Input
                                    value={editingUser.stripe_customer_id}
                                    disabled
                                    className="font-mono text-sm"
                                  />
                                </div>
                              )}
                              {editingUser?.subscription_started_at && (
                                <div className="text-sm text-muted-foreground">
                                  Prenumeration startad: {formatDate(editingUser.subscription_started_at)}
                                </div>
                              )}
                            </div>
                            <DialogFooter>
                              <Button
                                onClick={handleSaveUser}
                                disabled={saving}
                              >
                                {saving ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Sparar...
                                  </>
                                ) : (
                                  'Spara ändringar'
                                )}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          <p className="text-sm text-muted-foreground">
            Visar {filteredUsers.length} av {users.length} användare
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
